import axios from 'axios'
import {
  FacebookLogin,
  LinkedInLogin,
  SignInForm,
  SignUpForm,
  UpdateUserForm,
  User,
} from '../types/user'
import { CreateBook } from '../types/book'
import { CommentForm, EvaluationForm } from '../types/comment'
import { Payment } from '../types/payment'
import { CreateAuthor } from '../types/author'
import { CreatePublisher } from '../types/publisher'
import { RateForm } from '../types/rating'

const instance = axios.create({
  baseURL: 'https://bookly.pyunik.es/api',
  // baseURL: 'http://localhost:7000/api',
  withCredentials: true,
  headers: {
    Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY2ZjkyNWMyYzc4Y2ZkZmFiYjFlNjA1OCIsIm5hbWUiOiJBcm1hbiIsInN1cm5hbWUiOiJHaGFyaWJ5YW4iLCJlbWFpbCI6ImFybWFuLmdoYXJpYnlhbjE5OTNAZ21haWwuY29tIiwiaWF0IjoxNzI3NjA0MzQ1LCJleHAiOjE3MzAxOTYzNDV9.vDjsXb6ONgfm2DYDDjkPodWL3Rs5M-yLJ1oYjwY4YC8`,
  },
})

export const sliderImagesApi = {
  getMainSliderImages: async () => {
    const response = await instance.get('/slider-images')
    return response?.data?.sliderImages || []
  },
}

export const userAPi = {
  signUpUser: (signup: SignUpForm) => {
    return instance.post('/users/signup', signup)
  },

  signInUser: async (signIn: SignInForm) => {
    const response = await instance.post('/users/login', signIn)
    return response?.data?.authUser
  },

  verifyUser: async () => {
    const response = await instance.get('/users/verify-user')
    return response?.data?.authUser
  },

  confirmEmail: (token: string) => {
    return instance.post('/users/email_actvation', { token })
  },

  facebookLogin: (data: FacebookLogin) => {
    return instance.post('/facebook_login', data)
  },

  linkedInLogin: (data: LinkedInLogin) => {
    return instance.post('/linked_in_login', data)
  },

  getUserAuthorizedId: () => {
    return instance.get('/authorize_user')
  },

  getUserOrders: (id: string) => {
    return instance.get(`/user/orders/${id}`)
  },

  getUserData: (id: string) => {
    return instance.get(`/users/${id}`)
  },

  updateUser: async (user: UpdateUserForm) => {
    const response = await instance.post('/users/update-user', user)
    return response?.data?.authUser
  },

  updateUserPassword: (data: any) => {
    return instance.put('/user/save_new_password', data)
  },

  logout: () => {
    return instance.delete(`/users/logout`)
  },
}

export const bookApi = {
  getPopularBooks: async () => {
    const response = await instance.get(`/books/popular`, {
      params: {
        limit: 10,
      },
    })
    return response?.data?.books || []
  },

  getNewBooks: async (count: number) => {
    const response = await instance.get(`/books`, {
      params: {
        limit: count,
      },
    })
    return response?.data?.books || []
  },

  getBooksByCategory: async (category: string) => {
    const response = await instance.get(`/books`, {
      params: {
        category,
      },
    })
    return response?.data?.books || []
  },

  getBook: async (id: string) => {
    const response = await instance.get(`/books/${id}`)
    return response.data?.book
  },

  createBook: (book: CreateBook) => {
    return instance.post('/books', book)
  },

  rateBook: async (rate: RateForm) => {
    const response = await instance.post(`/books/${rate.id}/rate-book`, {
      rating: rate.rate,
    })
    return response.data?.averageRating
  },

  commentBook: async (commentForm: CommentForm) => {
    const response = await instance.post(
      `/books/${commentForm.bookId}/comment-book`,
      {
        text: commentForm.text,
      },
    )
    const comment = response.data?.comment
    return { ...comment, id: comment._id }
  },

  getBasketBooks: async () => {
    const response = await instance.get('/books/get-basket-books')
    return response.data?.basketBooks.map((book: any) => ({
      ...book,
      book: { ...book.book, id: book.book!._id },
    }))
  },

  addBookToBasket: async (bookId: string) => {
    const response = await instance.get(`/books/${bookId}/add-book-to-basket`)
    return response.data?.basketBooks.map((book: any) => ({
      ...book,
      book: { ...book.book, id: book.book!._id },
    }))
  },

  removeBookFromBasket: async (bookId: string) => {
    const response = await instance.get(
      `/books/${bookId}/remove-book-from-basket`,
    )
    return response.data?.basketBooks.map((book: any) => ({
      ...book,
      book: { ...book.book, id: book.book!._id },
    }))
  },
}

export const authorApi = {
  getAuthor: async (id: string) => {
    const response = await instance.get(`/authors/${id}`)
    return response.data?.author
  },

  getAllAuthors: async () => {
    const response = await instance.get('/authors')
    return response?.data?.authors || []
  },

  createAuthor: async (author: CreateAuthor) => {
    return (await instance.post(`/authors`, author))?.data?.author
  },
}

export const publisherApi = {
  getPublisher: async (id: string) => {
    const response = await instance.get(`/publishers/${id}`)
    return response.data?.publisher
  },

  getPublishers: async () => {
    const response = await instance.get('/publishers')
    return response?.data?.publishers || []
  },

  createPublisher: async (publisher: CreatePublisher) => {
    return (await instance.post(`/publishers`, publisher)).data.publisher
  },
}

export const commentApi = {
  getAllComments: () => {
    return instance.get('/comments')
  },

  evaluateComment: async (evaluationForm: EvaluationForm) => {
    const response = await instance.post('/comments/evaluate', evaluationForm)
    return response.data.comment
  },

  unEvaluateComment: async (commentId: string) => {
    const response = await instance.post('/comments/unevaluate', { commentId })
    return response.data.comment
  },

  deleteComment: async (id: string) => {
    const response = await instance.delete(`/comments/${id}`)
    return response.data.commentId
  },
}

export const basketApi = {
  getBasketBooks: () => {
    return instance.get('/basket')
  },

  addBookToBasket: (data: any) => {
    return instance.post('/basket', data)
  },

  removeBookFromBasket: (id: string) => {
    return instance.delete(`/basket/${id}`)
  },
}

export const searchApi = {
  searchBooksByName: async (name: string, language: string) => {
    const response = await instance.get(`/search/${name}/${language}`)
    return {
      foundBooks: response?.data?.books || [],
      foundAuthors: response?.data?.authors || [],
      foundPublishers: response?.data?.publishers || [],
    }
  },
}

export const paymentApi = {
  getShippingAdresses: (id: string) => {
    return instance.get(`/payment/shipping_address/${id}`)
  },

  saveShippingAdresses: (data: any) => {
    return instance.post('/payment/shipping_address', data)
  },

  makePayment: (data: Payment) => {
    return instance.post('/payment/stripe', data)
  },
}
